import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Copy, Check, ExternalLink, Bug, Eye, User, ChevronDown, ChevronUp } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { SystemEventLevelBadge } from './SystemEventLevelBadge';
import { SystemEventSourceBadge } from './SystemEventSourceBadge';
import { cn } from '@/lib/utils';
import type { SystemEvent } from '@/hooks/useSystemEvents';

interface SystemEventDetailDialogProps {
  event: SystemEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const levelBorderMap = {
  error: 'border-l-red-500',
  warn: 'border-l-amber-500',
  info: 'border-l-blue-500',
} as const;

export function SystemEventDetailDialog({
  event,
  open,
  onOpenChange,
}: SystemEventDetailDialogProps) {
  const [copied, setCopied] = useState(false);
  const [requestIdCopied, setRequestIdCopied] = useState(false);
  const [showTechnical, setShowTechnical] = useState(false);

  const handleCopyRequestId = useCallback(async () => {
    if (!event?.request_id) return;
    try {
      await navigator.clipboard.writeText(event.request_id);
      setRequestIdCopied(true);
      setTimeout(() => setRequestIdCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [event?.request_id]);

  if (!event) return null;

  const handleCopyContext = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(event.context, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (date: string) => {
    return format(new Date(date), "d MMM yyyy 'kl.' HH:mm:ss", { locale: sv });
  };

  const producerId = event.producer?.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader className="pb-0">
          <DialogTitle className="sr-only">Händelsedetalj</DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4 max-h-[70vh]">
          <div className="space-y-5">
            {/* ── Top summary card ── */}
            <div className={cn(
              'border-l-4 rounded-md bg-muted/40 p-4 space-y-3',
              levelBorderMap[event.level] ?? 'border-l-border'
            )}>
              {/* Row 1: level + event_key + source */}
              <div className="flex flex-wrap items-center gap-2">
                <SystemEventLevelBadge level={event.level} />
                <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">
                  {event.event_key}
                </code>
                <SystemEventSourceBadge source={event.source} />
                {event.domain && (
                  <Badge variant="outline" className="text-xs">{event.domain}</Badge>
                )}
              </div>

              {/* Row 2: message */}
              <p className="text-sm font-medium text-foreground leading-snug">
                {event.message}
              </p>

              {/* Row 3: key metadata inline */}
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
                <span>{formatDate(event.created_at)}</span>
                {event.producer && (
                  <Link
                    to={`/app/admin/producers/${event.producer.id}`}
                    className="text-primary hover:underline inline-flex items-center gap-1"
                    onClick={() => onOpenChange(false)}
                  >
                    <User className="h-3 w-3" />
                    {event.producer.name}
                  </Link>
                )}
                {event.request_id && (
                  <button
                    type="button"
                    onClick={handleCopyRequestId}
                    className="inline-flex items-center gap-1 font-mono hover:text-foreground transition-colors cursor-pointer"
                    title={event.request_id}
                  >
                    {event.request_id.length > 16
                      ? `${event.request_id.slice(0, 8)}…${event.request_id.slice(-4)}`
                      : event.request_id}
                    {requestIdCopied ? (
                      <Check className="h-3 w-3 text-emerald-600" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* ── Quick actions ── */}
            {producerId && (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" asChild>
                  <Link
                    to={`/app/admin/system/host-debug?q=${producerId}`}
                    onClick={() => onOpenChange(false)}
                  >
                    <Bug className="h-3.5 w-3.5 mr-1.5" />
                    Host Debug
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link
                    to={`/app/admin/producers/${producerId}?support_preview=1`}
                    onClick={() => onOpenChange(false)}
                  >
                    <Eye className="h-3.5 w-3.5 mr-1.5" />
                    Visa som värd
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link
                    to={`/app/admin/producers/${producerId}`}
                    onClick={() => onOpenChange(false)}
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                    Öppna värd
                  </Link>
                </Button>
                {event.request_id && (
                  <Button variant="outline" size="sm" asChild>
                    <Link
                      to={`/app/admin/system-logs?requestId=${event.request_id}`}
                      onClick={() => onOpenChange(false)}
                    >
                      Visa samma request
                    </Link>
                  </Button>
                )}
              </div>
            )}

            {/* ── Technical details (collapsible) ── */}
            <Separator />
            <button
              type="button"
              onClick={() => setShowTechnical(v => !v)}
              className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full"
            >
              {showTechnical ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              Tekniska detaljer
            </button>

            {showTechnical && (
              <div className="space-y-4 pl-1">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {event.entity_type && (
                    <MetadataRow label="Entitetstyp" value={event.entity_type} />
                  )}
                  {event.entity_id && (
                    <MetadataRow
                      label="Entitets-ID"
                      value={
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono break-all">
                          {event.entity_id}
                        </code>
                      }
                    />
                  )}
                  {event.actor_user_id && (
                    <MetadataRow
                      label="Användare (ID)"
                      value={
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono break-all">
                          {event.actor_user_id}
                        </code>
                      }
                    />
                  )}
                  {event.ip && (
                    <MetadataRow label="IP" value={event.ip} />
                  )}
                </div>

                {event.user_agent && (
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-1">User Agent</h4>
                    <p className="text-xs text-muted-foreground bg-muted p-2 rounded font-mono break-all">
                      {event.user_agent}
                    </p>
                  </div>
                )}

                {/* Context JSON */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-xs font-medium text-muted-foreground">Kontext (JSON)</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCopyContext}
                      className="h-7 text-xs"
                    >
                      {copied ? (
                        <>
                          <Check className="h-3 w-3 mr-1" />
                          Kopierat
                        </>
                      ) : (
                        <>
                          <Copy className="h-3 w-3 mr-1" />
                          Kopiera
                        </>
                      )}
                    </Button>
                  </div>
                  <pre className="text-xs bg-muted p-3 rounded font-mono overflow-x-auto max-h-64">
                    {JSON.stringify(event.context, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="flex justify-end pt-3 border-t">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Stäng
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MetadataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs mb-0.5">{label}</dt>
      <dd className="text-foreground text-sm">{value}</dd>
    </div>
  );
}
